import { useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";

import { AppDialog } from "./components/AppDialog";
import { BandeauPetitEcran } from "./components/BandeauPetitEcran";
import { ChuteAuJalon } from "./screens/Fins/ChuteAuJalon";
import { Crise } from "./screens/Crise/Crise";
import { EvenementModal } from "./components/EvenementModal";
import { InfoBulleDecouverte } from "./components/InfoBulleDecouverte";
import { ModeDecouverte } from "./components/ModeDecouverte";
import { MomentsDebloques } from "./components/MomentsDebloques";
import { PanneauDev } from "./components/PanneauDev";
import { PopupsPalier } from "./components/PopupsPalier";
import { RecapJours } from "./components/RecapJours";
import { SoundProvider, useSfx } from "./components/SoundProvider";
import { AmbianceSM } from "./audio/AmbianceSM";
import { SonsDuJeu } from "./audio/SonsDuJeu";
import { Toasts } from "./components/Toasts";
import { zoneSM } from "./game/format";
import { Routeur } from "./router";
import { RepriseAtelier } from "./screens/Atelier/RepriseAtelier";
import { Accueil } from "./screens/Accueil/Accueil";
import { Authentification } from "./screens/Compte/Authentification";
import { Intro } from "./screens/Intro/Intro";
import { Menu } from "./screens/Menu/Menu";
import { useAccueilStore } from "./stores/accueilStore";
import { useIntroStore } from "./stores/introStore";
import { useRunStore } from "./stores/runStore";
import { useSessionStore } from "./stores/sessionStore";
import { useUIStore } from "./stores/uiStore";

/**
 * La coquille. Trois responsabilités et pas une de plus :
 * ouvrir la porte d'entrée puis restituer la run, poser la zone de santé mentale sur la racine
 * (SPEC-UI §7.5), et monter les trois surfaces globales — dialogue maison (§7.1), toasts,
 * moments de jeu.
 *
 * ⚠️ **L'ordre compte** : `sessionStore.initialiser()` décide d'abord *où* la partie est
 * calculée (bac à sable ou compte, `game/client.ts`), et c'est lui qui appelle
 * `runStore.charger()` quand la réponse est connue. Charger avant de savoir interrogerait le
 * bac à sable pour un joueur qui a un compte — et lui montrerait une autre partie que la sienne.
 */
export function App() {
  return (
    <SoundProvider>
      <Coquille />
    </SoundProvider>
  );
}

function Coquille() {
  const initialiser = useSessionStore((s) => s.initialiser);
  const statut = useSessionStore((s) => s.statut);
  const pret = useRunStore((s) => s.pret);
  const run = useRunStore((s) => s.run);
  const mental = useRunStore((s) => s.run?.state.mental ?? 100);
  // ⟦M2.7⟧ La chute (game over en mode normal) prend tout l'écran : c'est le moment le plus
  // dramatique du jeu, et il était invisible jusqu'ici.
  const chute = useRunStore((s) => s.chute);
  const consommerChute = useRunStore((s) => s.consommerChute);
  // ⟦SPEC-UI §7.5 · A-83⟧ La crise de SM = 0 : « écran dédié », plein cadre, AVANT la chute ou
  // la fin qu'elle a pu déclencher — on raconte d'abord ce qui vient d'arriver.
  const crise = useRunStore((s) => s.crise);
  const consommerCrise = useRunStore((s) => s.consommerCrise);
  const etatCourant = useRunStore((s) => s.run?.state ?? null);
  const nomDeChaine = useRunStore((s) => s.run?.channelName ?? "");
  const setReducedMotion = useUIStore((s) => s.setReducedMotion);
  const sfx = useSfx();
  // ⟦C22 · A-151⟧ Voir `stores/introStore.ts` : montée ICI, au-dessus de `<Routeur/>`, PAS dans
  // `NouvelleRun.tsx` : `run` (posé dans `runStore` dès que `creer()` résout) fait basculer
  // `Routeur` de cet écran vers la chambre à la même milliseconde, et démonterait avec lui un
  // `<Intro>` qui y aurait été niché, avant même sa première image (constaté en jouant).
  const introActif = useIntroStore((s) => s.actif);
  const introNomChaine = useIntroStore((s) => s.nomChaine);
  const introGenre = useIntroStore((s) => s.genre);
  const introPret = useIntroStore((s) => s.pret);
  const introTerminer = useIntroStore((s) => s.terminer);
  // ⟦C27 · A-153⟧ L'accueil (les 4 répliques de l'accueil, avec la voix, puis le glissement de
  // l'interface) prend le relais de l'intro, même raison de vivre ICI, au-dessus de `<Routeur/>`
  // plutôt que dans `NouvelleRun.tsx` (voir `stores/introStore.ts`) : la VRAIE chambre est déjà
  // montée dessous, cet overlay se contente de la couvrir puis de s'effacer.
  const accueilActif = useAccueilStore((s) => s.actif);
  const accueilGenre = useAccueilStore((s) => s.genre);
  const accueilGlissement = useAccueilStore((s) => s.glissement);
  const accueilDemarrer = useAccueilStore((s) => s.demarrer);
  const accueilLancerGlissement = useAccueilStore((s) => s.lancerGlissement);
  const accueilTerminer = useAccueilStore((s) => s.terminer);

  // `ui_click` sur « tout bouton » (§7.6) : un seul écouteur en capture plutôt que dix-sept
  // `onClick` à ne pas oublier. Les boutons désactivés ne sonnent pas.
  useEffect(() => {
    const auClic = (e: MouseEvent) => {
      const cible = (e.target as HTMLElement | null)?.closest("button");
      if (cible && !(cible as HTMLButtonElement).disabled) sfx("ui_click");
    };
    document.addEventListener("click", auClic, true);
    return () => document.removeEventListener("click", auClic, true);
  }, [sfx]);

  useEffect(() => {
    void initialiser();
  }, [initialiser]);

  // `prefers-reduced-motion` se lit, ne se stocke pas (SPEC-UI §7.4).
  useEffect(() => {
    const mq = matchMedia("(prefers-reduced-motion: reduce)");
    const maj = () => setReducedMotion(mq.matches);
    maj();
    mq.addEventListener("change", maj);
    return () => mq.removeEventListener("change", maj);
  }, [setReducedMotion]);

  const zone = zoneSM(mental);
  // ⟦C18 bis · constat 2⟧ Voir le commentaire au-dessus de `<BandeauPetitEcran/>` : couvre
  // Crise, ChuteAuJalon et les trois branches de `Routeur()` où `run` existe, rien d'autre.
  const enPartie = statut !== "boot" && statut !== "menu" && statut !== "auth" && !!run;

  return (
    <BrowserRouter>
      <div id="app" className={`app zone-${zone}`}>
        {/* ⟦C22 · A-151⟧ L'intro d'ouverture de chaîne, voir le commentaire sur `introActif`
            plus haut pour le pourquoi de sa place ICI plutôt que dans `NouvelleRun.tsx`. La
            VRAIE chambre est déjà montée dessous (`Routeur` a déjà basculé) : l'overlay opaque
            de l'intro la couvre le temps du minutage, `terminer()` ne fait que la retirer. */}
        {introActif && introGenre ? (
          <Intro
            nomChaine={introNomChaine}
            genre={introGenre}
            pret={introPret}
            onTermine={() => {
              // ⟦C27 · A-153⟧ L'intro cède directement la place à l'accueil (jamais un aller-
              // retour par l'interface entre les deux) : les deux appels sont synchrones, dans
              // le même geste, pour que React les batch dans le même rendu, aucune frame où
              // ni l'un ni l'autre n'occupe l'écran.
              introTerminer();
              accueilDemarrer(introGenre);
            }}
          />
        ) : null}
        {accueilActif && accueilGenre ? (
          <Accueil
            genre={accueilGenre}
            glissement={accueilGlissement}
            onGlisser={accueilLancerGlissement}
            onTermine={accueilTerminer}
          />
        ) : null}
        {/* ⟦A-141 · C17⟧ Toutes les surfaces de CE bloc lisent `runStore` directement, sans
            regarder `sessionStore.statut` — correct tant que « une run chargée » ⇒ « on est en
            train d'y jouer ». Ce n'est plus vrai depuis « Menu principal » (`Reglages.tsx`) :
            revenir à l'écran-titre NE VIDE PLUS la run (⟦A-141⟧, pour que « Charger » y
            reprenne d'un clic) — sans ce garde, la leçon du mode découverte, un événement en
            attente ou l'ambiance de zone rouge d'une chaîne en péril continuaient de se jouer
            PAR-DESSUS le menu. Trouvé en testant le parcours, pas prévu par le brief. */}
        {statut === "jeu" ? (
          <>
            {/* ⟦A-76 · A-160⟧ SPEC-UI §7.5 : une ambiance par zone de SM (vert/fatigue/rouge).
                Composant sans rendu, à la racine parce que la zone de SM est un état de l'app,
                pas d'un écran — il ne vole jamais le canal musique d'un écran qui le tient. */}
            <AmbianceSM />
            {/* ⟦SPEC-UI §7.6⟧ Les sons d'action — cash_in / cash_out / sleep_transition — à la
                racine pour la même raison : un mouvement de Clicks est un fait du tour. */}
            <SonsDuJeu />
            {/* ⟦SPEC-UI §1.7 · A-83⟧ Au boot, une production en cours se propose à la reprise
                (dialogue maison). Sous le routeur : il lui faut `useNavigate`. */}
            <RepriseAtelier />
          </>
        ) : null}
        {/* ⟦C18 bis · constat 2⟧ « Joue sur un écran plus large » se monte ICI, une seule fois,
            au niveau de la coquille — PAS dans `router.tsx`. La première version ⟦C18⟧ l'y avait
            posé sur trois branches (fin, victoire, routeur par défaut), une règle recopiée trois
            fois qui a laissé un trou : `<Crise/>` et `<ChuteAuJalon/>`, juste en dessous, sont
            rendus par CETTE coquille SANS jamais passer par `Routeur()` — le bandeau y avait
            disparu, une régression (il était global avant ⟦C18⟧). `enPartie` couvre exactement
            les cinq écrans concernés (Crise, ChuteAuJalon, et les trois branches de `Routeur()`
            où une run existe : fin, victoire, chambre/Atelier) d'une seule condition, et exclut
            le menu et les trois étapes de création — y compris quand une run reste chargée en
            mémoire après un aller « Menu principal » (⟦A-141⟧) : le bandeau ne juge que
            `statut`, jamais seulement « une run traîne dans le store ».
            ⟦C21 · A-146 §2⟧ Sur la chambre précisément, `SurfacesHorsChambre` retire ce bandeau
            (repris en réplique de Matt, `useMattChambre`) ET `ModeDecouverte`/
            `InfoBulleDecouverte` (remplacés par la même boîte) — les trois restent montés tels
            quels sur les écrans qui n'ont pas Matt en hôte (Atelier, Fin, Victoire, Crise,
            ChuteAuJalon). */}
        {enPartie ? <SurfacesHorsChambre /> : null}
        {statut === "boot" ? (
          <p className="boot">Ouverture du studio…</p>
        ) : statut === "menu" ? (
          <Menu />
        ) : statut === "auth" ? (
          <Authentification />
        ) : !pret ? (
          <p className="boot">Ouverture du studio…</p>
        ) : crise && etatCourant ? (
          <Crise crise={crise} state={etatCourant} nomDeChaine={nomDeChaine} onContinuer={consommerCrise} />
        ) : chute ? (
          <ChuteAuJalon retour={chute} nomDeChaine={nomDeChaine} onContinuer={consommerChute} />
        ) : (
          <Routeur />
        )}
        {/* ⟦A-141 · C17⟧ Même garde que le bloc audio plus haut : ces surfaces racontent le
            TOUR qui vient de se jouer (jours écoulés, palier, déverrouillage, événement,
            leçon) — sans le garde, elles continuaient de s'afficher PAR-DESSUS l'écran-titre
            depuis « Menu principal », qui laisse la run chargée exprès. `AppDialog`/`Toasts`
            restent hors du garde : `Menu.tsx` s'en sert directement (`dialog.confirm`, les
            toasts de `chargerPartie()`). */}
        {statut === "jeu" ? (
          <>
            {/* ⟦A-16⟧ Le récapitulatif des jours écoulés passe DEVANT le palier, le
                déverrouillage et l'événement : on raconte d'abord ce que ces jours ont coûté,
                on fête ensuite. Il s'efface en revanche derrière la leçon du mode découverte,
                qui explique avant.
                ⟦C21 · A-146 §2⟧ `ModeDecouverte`/`InfoBulleDecouverte` ne sont plus montés ici :
                sur la chambre, Matt (`useMattChambre`, `Studio.tsx`) les remplace tous les deux
                dans la MÊME boîte — ils restent branchés pour les écrans qui n'ont pas Matt en
                hôte, via `SurfacesHorsChambre` juste au-dessus. */}
            <RecapJours />
            <PopupsPalier />
            <MomentsDebloques />
            <EvenementModal />
          </>
        ) : null}
        <AppDialog />
        <Toasts />
        {/* ⟦A-176 · C59⟧ Le mode triche (ex-télécommande de dev), au-dessus de tout, y compris du
            mode découverte qui fige le jeu. Monté en production aussi : il ne rend RIEN tant qu'il
            n'est pas armé (`?triche`, code Konami, sept tapes sur la télé MeTube) et rien du tout
            en mode compte, mais il écoute le clavier et les tapes pour pouvoir s'armer. */}
        <PanneauDev />
      </div>
    </BrowserRouter>
  );
}

/**
 * ⟦C21 · A-146 §2⟧ Les trois surfaces que Matt reprend à son compte SUR LA CHAMBRE
 * (`BandeauPetitEcran`, `ModeDecouverte`, `InfoBulleDecouverte`) — montées ici seulement HORS
 * chambre, où elles gardent tout leur comportement d'avant ce chantier au caractère près.
 *
 * « La chambre », au sens de ce garde : tout ce que `router.tsx` place SOUS `<Studio/>`
 * (`/`, `/nouvelle-video`, `/journal`, `/arbre`…) — Studio et ses panneaux ne sont jamais
 * démontés entre ces routes, c'est le même écran avec un panneau posé par-dessus (⟦A-7⟧
 * décision 4). Les deux routes qui en sortent vraiment sont `/atelier` (plein écran) et `/fin` ;
 * `Crise`/`ChuteAuJalon` sont rendus par `Coquille` SANS passer par `Routeur()`, donc jamais
 * sous ce chemin non plus. Une run non active (palmarès/historique consultés après une fin,
 * `router.tsx` deuxième jeu de routes) n'est déjà plus « la chambre » : `Studio` ne l'enveloppe
 * plus dans cet état, d'où le test sur `state.status`.
 */
function SurfacesHorsChambre() {
  const location = useLocation();
  const crise = useRunStore((s) => s.crise);
  const chute = useRunStore((s) => s.chute);
  const statutRun = useRunStore((s) => s.run?.state.status ?? null);
  const surLaChambre =
    !crise &&
    !chute &&
    statutRun === "active" &&
    location.pathname !== "/atelier" &&
    location.pathname !== "/fin";

  if (surLaChambre) return null;
  return (
    <>
      <BandeauPetitEcran />
      <ModeDecouverte />
      <InfoBulleDecouverte />
    </>
  );
}
